import React, { useState } from 'react';
import { Lock, User, ShieldCheck, ArrowRight } from 'lucide-react';
import { useDialog } from '../ui/DialogProvider.js';

interface LoginViewProps {
  onLogin: (user: any, token?: string) => void;
}

export const LoginView: React.FC<LoginViewProps> = ({ onLogin }) => {
  const { showAlert } = useDialog();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      showAlert('Mohon lengkapi username dan password', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const { api } = await import('../../services/api.js');
      const response = await api.login({ username, password });

      if (response.success && response.user) {
        if (response.role === 'Siswa/Wali') {
          showAlert('Anda login sebagai Siswa. Mengalihkan ke Halaman Mobile...', { type: 'success' });
          localStorage.setItem('portal_token', response.token);
          localStorage.setItem('portal_siswa_id', response.user.id);
          setTimeout(() => {
            window.location.href = '/mobile';
          }, 1500);
          return;
        }

        onLogin({
          ...response.user,
          // Ensuring frontend expects proper naming
          nama: response.user.nama,
          levelUser: response.user.levelUser,
          username: response.user.username,
        }, response.token);
      } else {
        showAlert(response.message || 'Username atau password salah.', { type: 'danger' });
      }
    } catch (error: any) {
      console.error('Login error:', error);
      showAlert(error?.message || 'Gagal terhubung ke server database.', { type: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex text-slate-800">

      {/* Left Pane - Branding & Graphic */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-blue-600 to-sky-500 overflow-hidden">
        {/* Background Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-[20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-white/10 blur-3xl mix-blend-overlay"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full bg-sky-300/20 blur-3xl mix-blend-overlay"></div>
        </div>

        <div className="relative z-10 w-full flex flex-col justify-center px-20">
          <div className="mb-12 inline-block">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-blue-600 shadow-xl font-bold text-2xl">
              SD
            </div>
          </div>
          <h1 className="text-5xl font-extrabold text-white mb-6 leading-tight">
            Sistem Pembayaran <br />
            & Payment Gateway <br />
            SDMT Ponorogo
          </h1>
          <p className="text-blue-100 text-lg max-w-lg leading-relaxed mb-12">
            Platform terpadu untuk manajemen keuangan sekolah, terintegrasi dengan Duitku Payment Gateway dan Aplikasi Mobile Wali Murid secara Real-Time.
          </p>

          <div className="flex items-center gap-4 text-blue-100 bg-white/10 p-4 rounded-2xl backdrop-blur-sm border border-white/20 w-max">
            <ShieldCheck size={28} className="text-white" />
            <div>
              <div className="font-semibold text-white">Sistem Keamanan Aktif</div>
              <div className="text-sm opacity-80">Enkripsi End-to-End</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Pane - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white relative">
        {/* Mobile Logo Fallback */}
        <div className="absolute top-8 left-8 lg:hidden">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-sky-500 rounded-xl flex items-center justify-center text-white shadow font-bold">
              SD
            </div>
            <span className="font-bold text-slate-800 tracking-tight">SDMT Ponorogo</span>
          </div>
        </div>

        <div className="w-full max-w-md">
          <div className="mb-10 text-center lg:text-left">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Selamat Datang</h2>
            <p className="text-slate-500">Silakan login ke portal administrator keuangan.</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Username</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                  <User size={20} />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-slate-800 placeholder-slate-400"
                  placeholder="Masukkan username"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                  <Lock size={20} />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-slate-800 placeholder-slate-400"
                  placeholder="Masukkan password"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300" />
                <span className="text-sm text-slate-600">Ingat Saya</span>
              </label>
              <a href="#" className="text-sm font-medium text-blue-600 hover:text-blue-700">Lupa Password?</a>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center gap-2 py-3.5 px-4 bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white rounded-xl font-bold shadow-lg shadow-blue-500/30 transition-all disabled:opacity-70"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  Masuk Sekarang
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>

          <div className="mt-10 text-center text-sm text-slate-500">
            © 2026 SD Muhammadiyah Terpadu Ponorogo <br />
            Versi 2.4.0
          </div>
        </div>
      </div>

    </div>
  );
};
